import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FileDown } from "lucide-react";
import { Alert, Button, ErrorState, PageHeader, Panel, SelectField, Skeleton, TextField } from "../components/ui.tsx";
import { useToast } from "../components/toast.tsx";
import { download, get } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import type { Axis, Group, Unit } from "../lib/types.ts";
import { STATUS_LABEL, type InitiativeStatus } from "../../shared/planning.ts";
import { usePageTitle, useUrlState } from "../lib/nav.tsx";

type Kind = "plan" | "performance" | "review" | "planning" | "agenda";
const REPORTS: { kind: Kind; title: string; text: string; files: { label: string; path: string }[] }[] = [
  { kind: "plan", title: "Plano completo", text: "Hierarquia eixo → objetivo → KR → iniciativa, ações com datas e pesos, situação do planejamento e apêndice da agenda.", files: [{ label: "Gerar PDF", path: "plan.pdf" }] },
  { kind: "performance", title: "Relatório de desempenho", text: "Uma tabela por eixo: objetivo, KR, responsável, iniciativa, progresso da execução e cronograma.", files: [{ label: "Gerar PDF", path: "performance.pdf" }] },
  { kind: "review", title: "Relatório de Revisão de Planejamento", text: "Situação atual, versão submetida, decisões do instrutor e pendências. Pode incluir o histórico de versões.", files: [{ label: "Gerar PDF", path: "review.pdf" }] },
  { kind: "planning", title: "Planejamento em planilha", text: "Uma linha por ação (rascunhos sem ações aparecem em uma linha).", files: [{ label: "Gerar CSV", path: "planning.csv" }] },
  { kind: "agenda", title: "Agenda", text: "Agenda completa com tarefas, ou calendário só com mês e dia dos eventos.", files: [{ label: "Gerar PDF", path: "agenda.pdf" }, { label: "Calendário de eventos (PDF)", path: "agenda-calendar.pdf" }, { label: "Gerar CSV", path: "agenda.csv" }] },
];

interface Preview {
  filters: string[]; initiatives?: number; actions?: number; by_status?: Record<InitiativeStatus, number>; with_pending_fields?: number;
  csv_rows: number; events?: number; tasks?: number; incomplete_events?: number;
}

/** T12: real files generated on the server; the preview is only a count summary. */
export function ReportsPage() {
  const plan = usePlan();
  const toast = useToast();
  usePageTitle("Relatórios", plan.name);
  // Report type and filters live in the URL so returning to this page restores them (UX-02).
  const [u, setU] = useUrlState({
    tipo: "plan", axis_id: "", unit_id: "", status: "", from: "", to: "", undated: "", committee: "", group_id: "", agenda: "1", historico: "",
  });
  const kind = (["plan", "performance", "review", "planning", "agenda"].includes(u.tipo) ? u.tipo : "plan") as Kind;
  const setKind = (k: Kind) => setU({ tipo: k });
  const s = { axis_id: u.axis_id, unit_id: u.unit_id, status: u.status };
  const a = { from: u.from, to: u.to, undated: u.undated, committee: u.committee, group_id: u.group_id };
  const setS = (v: typeof s) => setU(v);
  const setA = (v: typeof a) => setU(v);
  const includeAgenda = u.agenda === "1";
  const setIncludeAgenda = (v: boolean) => setU({ agenda: v ? "1" : "0" });
  const includeHistory = u.historico === "1";
  const setIncludeHistory = (v: boolean) => setU({ historico: v ? "1" : "" });
  const [busy, setBusy] = useState<string | null>(null);

  const strategic = kind !== "agenda";
  const agendaFilters = kind === "agenda" || (kind === "plan" && includeAgenda);
  const clean = (o: Record<string, string>) => Object.fromEntries(Object.entries(o).filter(([, v]) => v));
  const previewQs = new URLSearchParams({ type: kind, ...(strategic ? clean(s) : clean(a)) }).toString();
  const preview = useQuery({
    queryKey: planKey(plan.id, "export-preview", previewQs),
    queryFn: async () => (await get<{ data: Preview }>(`/plans/${plan.id}/export-preview?${previewQs}`)).data,
    placeholderData: keepPreviousData,
  });
  const axes = useQuery({ queryKey: planKey(plan.id, "axes"), queryFn: async () => (await get<{ data: Axis[] }>(`/plans/${plan.id}/axes`)).data });
  const units = useQuery({ queryKey: planKey(plan.id, "units"), queryFn: async () => (await get<{ data: Unit[] }>(`/plans/${plan.id}/units`)).data });
  const groups = useQuery({ queryKey: planKey(plan.id, "groups"), queryFn: async () => (await get<{ data: Group[] }>(`/plans/${plan.id}/groups`)).data });

  async function generate(path: string) {
    const body =
      path === "plan.pdf" ? { ...clean(s), include_agenda: includeAgenda, agenda_filter: clean(a) }
      : path === "review.pdf" ? { ...clean(s), include_history: includeHistory }
      : path === "planning.csv" || path === "performance.pdf" ? clean(s)
      : clean(a);
    setBusy(path);
    try {
      await download(`/plans/${plan.id}/exports/${path}`, body);
      toast("success", "Arquivo gerado.");
    } catch (e) {
      toast("danger", e instanceof Error ? e.message : "Não foi possível gerar o arquivo.");
    } finally {
      setBusy(null);
    }
  }

  const report = REPORTS.find((r) => r.kind === kind)!;
  const p = preview.data;
  return (
    <>
      <PageHeader title="Relatórios e exportações" subtitle="Arquivos gerados com os dados atuais que você pode consultar." />
      <div role="radiogroup" aria-label="Tipo de relatório" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {REPORTS.map((r) => (
          <button key={r.kind} type="button" role="radio" aria-checked={kind === r.kind} onClick={() => setKind(r.kind)}
            className={`lift rounded-panel border bg-surface p-4 text-left ${kind === r.kind ? "border-primary ring-2 ring-accent/40" : "border-line"}`}>
            <span className="block font-semibold">{r.title}</span>
            <span className="mt-1 block text-xs text-muted">{r.text}</span>
          </button>
        ))}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Panel>
          <h2 className="font-bold">Filtros</h2>
          {strategic && (
            <div className="grid gap-x-4 sm:grid-cols-3">
              <SelectField label="Eixo" value={s.axis_id} onChange={(e) => setS({ ...s, axis_id: e.target.value })}>
                <option value="">Todos</option>{(axes.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </SelectField>
              <SelectField label="Unidade" value={s.unit_id} onChange={(e) => setS({ ...s, unit_id: e.target.value })}>
                <option value="">Todas</option>{(units.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </SelectField>
              <SelectField label="Situação" value={s.status} onChange={(e) => setS({ ...s, status: e.target.value })}>
                <option value="">Todas</option>{(Object.keys(STATUS_LABEL) as InitiativeStatus[]).map((x) => <option key={x} value={x}>{STATUS_LABEL[x]}</option>)}
              </SelectField>
            </div>
          )}
          {kind === "plan" && (
            <label className="my-2 flex items-center gap-2 text-sm"><input type="checkbox" className="size-4 accent-primary" checked={includeAgenda} onChange={(e) => setIncludeAgenda(e.target.checked)} />Incluir apêndice da agenda</label>
          )}
          {kind === "review" && (
            <label className="my-2 flex items-center gap-2 text-sm"><input type="checkbox" className="size-4 accent-primary" checked={includeHistory} onChange={(e) => setIncludeHistory(e.target.checked)} />Incluir versões históricas</label>
          )}
          {agendaFilters && (
            <>
              {kind === "plan" && <h3 className="mt-3 text-sm font-bold">Filtros do apêndice da agenda</h3>}
              <div className="grid gap-x-4 sm:grid-cols-2 lg:grid-cols-3">
                <TextField label="Evento a partir de" type="date" value={a.from} onChange={(e) => setA({ ...a, from: e.target.value })} />
                <TextField label="Evento até" type="date" value={a.to} onChange={(e) => setA({ ...a, to: e.target.value })} />
                <SelectField label="Sem data" value={a.undated} onChange={(e) => setA({ ...a, undated: e.target.value })}>
                  <option value="">{a.from || a.to ? "Excluir sem data" : "Incluir sem data"}</option>
                  {(a.from || a.to) && <option value="include">Incluir sem data</option>}
                  <option value="only">Somente sem data</option>
                </SelectField>
                <TextField label="Comissão" value={a.committee} onChange={(e) => setA({ ...a, committee: e.target.value })} />
                <SelectField label="Grupo editor" value={a.group_id} onChange={(e) => setA({ ...a, group_id: e.target.value })}>
                  <option value="">Todos</option>{(groups.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </SelectField>
              </div>
            </>
          )}
        </Panel>
        <Panel className="h-fit">
          <h2 className="font-bold">{report.title}</h2>
          {preview.isPending ? <Skeleton lines={3} /> : preview.isError ? <ErrorState error={preview.error} onRetry={() => void preview.refetch()} /> : p && (
            <div className="mt-2 text-sm">
              {p.initiatives !== undefined && <p>{p.initiatives} iniciativa(s) · {p.actions} ação(ões)</p>}
              {p.by_status && <p className="text-xs text-muted">{(Object.keys(p.by_status) as InitiativeStatus[]).map((k) => `${p.by_status![k]} ${STATUS_LABEL[k].toLowerCase()}`).join(" · ")}</p>}
              {p.with_pending_fields ? <p className="text-xs text-muted">{p.with_pending_fields} com pendências de preenchimento</p> : null}
              {p.events !== undefined && <p>{p.events} ação(ões) da agenda · {p.tasks} tarefa(s){p.incomplete_events ? ` · ${p.incomplete_events} em elaboração` : ""}</p>}
              {kind.endsWith("planning") || kind === "agenda" ? <p className="text-xs text-muted">{p.csv_rows} linha(s) no CSV</p> : null}
              <p className="mt-2 text-xs text-muted">Filtros: {p.filters.length ? p.filters.join(" · ") : "nenhum"}</p>
            </div>
          )}
          <div className="mt-4 flex flex-col gap-2">
            {report.files.map((f) => (
              <Button key={f.path} loading={busy === f.path} disabled={busy !== null} onClick={() => void generate(f.path)}>
                <FileDown className="size-4" aria-hidden />{f.label}
              </Button>
            ))}
          </div>
          <Alert tone="info">Os relatórios mostram a situação do planejamento. Não há acompanhamento de execução nesta versão.</Alert>
        </Panel>
      </div>
    </>
  );
}
